import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Sparkles, ArrowRight, Baby } from "lucide-react";
import { useLang } from "@/context/LangContext";

const COLLECTIONS = [
  {
    key: "toys",
    title: "Toys",
    subtitle: "Channapatna dolls, wooden rattles, non-toxic playthings",
    img: "https://images.pexels.com/photos/3661359/pexels-photo-3661359.jpeg?w=800",
    tag: "toys",
    accent: "#E25A24",
  },
  {
    key: "books-games",
    title: "Books & Games",
    subtitle: "Panchatantra, Jataka tales, ludo, snakes-and-ladders",
    img: "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800",
    tag: "books",
    accent: "#13294B",
  },
  {
    key: "health-foods",
    title: "Health Foods & Snacks",
    subtitle: "Millet cookies, jaggery treats, ragi bars — no preservatives",
    img: "https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=800",
    tag: "health-food",
    accent: "#275F36",
  },
  {
    key: "stationeries",
    title: "Stationeries",
    subtitle: "Handmade paper diaries, wooden pencils, block-print notebooks",
    img: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800",
    tag: "stationery",
    accent: "#7C3F58",
  },
  {
    key: "sel",
    title: "Social & Emotional Learning",
    subtitle: "Mindfulness cards, feelings journals, empathy games",
    img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800",
    tag: "sel",
    accent: "#B44618",
  },
];

export default function GenAlpha() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { q: "genalpha", limit: 12 } })
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#13294B] text-white">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, #FFB07C 0, transparent 40%), radial-gradient(circle at 75% 70%, #FFDDC5 0, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-bold uppercase tracking-widest">
              <Baby className="w-3.5 h-3.5 text-[#FFDDC5]" /> Gen Alpha collection
            </div>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-white">
              Handmade in India, <br/>
              <span className="text-[#FFDDC5]">made for</span> tiny humans.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              Screen-free toys, illustrated stories, Ayurvedic baby care and
              handloom kids' wear — curated for the generation growing up
              between traditions and touchscreens. Non-toxic, hand-checked,
              parent-approved.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                data-testid="genalpha-shop-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFDDC5] text-[#0D141C] font-bold hover:bg-white transition-colors"
              >
                Shop the collection <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#collections"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                Browse categories
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-serif text-3xl text-[#FFDDC5]">0</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Plastic toys</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-[#FFDDC5]">100%</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Non-toxic</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-[#FFDDC5]">Age 0–12</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Curated for</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS BENTO */}
      <section id="collections" className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-10">
          <div className="label-caps mb-2">The bundle</div>
          <h2 className="text-3xl md:text-4xl">Everything a tiny human needs</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="genalpha-collections">
          {COLLECTIONS.map((c) => (
            <Link
              key={c.key}
              to={`/products?q=${encodeURIComponent(c.tag)}`}
              data-testid={`genalpha-collection-${c.key}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden card-flat"
            >
              <img
                src={c.img}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, transparent 0%, rgba(13,20,28,0.15) 55%, ${c.accent}e6 100%)`,
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Collection</div>
                <div className="font-serif text-2xl md:text-3xl leading-tight mt-1">{c.title}</div>
                <div className="text-sm opacity-90 mt-2 leading-snug">{c.subtitle}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRENDING */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="label-caps mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#E25A24]" /> Parents' favourites</div>
              <h2 className="text-3xl md:text-4xl">Beautifully made picks</h2>
            </div>
            <Link to="/products" className="text-sm text-[#E25A24] font-semibold hover:underline">Shop all →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="genalpha-products">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="bg-[#F2EFEB] py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="label-caps mb-4">Why it matters</div>
          <h2 className="text-4xl md:text-5xl leading-tight">
            Because the first thing they hold shouldn't be plastic.
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Every toy, book and garment here is checked for safety, sourced
            from Indian artisans, and made with the natural materials our
            grandparents grew up with. Non-toxic paints, hand-block prints,
            cold-pressed oils, cotton and wood — nothing that shouldn't be
            near a child.
          </p>
          <Link
            to="/products"
            className="mt-10 btn-primary inline-flex items-center gap-2"
            data-testid="genalpha-cta-bottom"
          >
            Enter the collection <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
